import { BarElement, CategoryScale, Chart, ChartOptions, Legend, LinearScale, Title, Tooltip } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { TFunction } from "i18next";
import * as React from "react";
import { Bar } from "react-chartjs-2";
import { Translation } from "react-i18next";

import style from "./chart.scss";
import { commonFunctions, Units } from "components/dashboard/esg/CommonFunctions";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { TextBlock } from "components/typography/textBlock/TextBlock";
import { DashboardDataList, DeviceType } from "domain/dashboard";
import { resolveColor } from "utils/commonFunctions";

interface ChartProps {
    displayLegend?: boolean;
    widgetData: DashboardDataList[];
    dataFetched: boolean;
    convertToTonnes: boolean;
}

interface ChartState {
    data: [];
    displayLegend?: boolean;
}

type Props = ChartProps;

Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);
class LandfillWastePreventedChart extends React.Component<Props, ChartState> {
    public static defaultProps = {
        displayLegend: false,
        convertToTonnes: false,
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            data: [],
            displayLegend: this.props.displayLegend,
        };
    }

    fetchLandfillWaste = (deviceType: DeviceType) => {
        const weights: number[] = [];
        if (this.props.widgetData.length > 0) {
            Object.values(this.props.widgetData).map((deviceData) => {
                Object.entries(deviceData.content).forEach(([key, value]) => {
                    if (deviceType === key) {
                        weights.push(this.props.convertToTonnes ? value.weight / 1000 : value.weight);
                    }
                });
            });
        }

        return weights;
    };

    public render(): JSX.Element {
        if (!this.props.dataFetched) {
            return (
                <div className={style.loaderContainer}>
                    <LoadingIndicator />
                </div>
            );
        }
        const displayedValuesAndUnits = commonFunctions.getDisplayedValueAndUnit(
            commonFunctions.computeTotalNumberOfErasedDevices(this.props.widgetData),
            commonFunctions.computeAverageWeightProcessPerDay(this.props.widgetData)
        );

        return (
            <Translation>
                {(t) => (
                    <div className={style.chartContainer}>
                        <div className={style.topSummaryContainer}>
                            <TextBlock>
                                <span className={style.summaryCount}>
                                    {t("EsgDashboardView.landfillWastePreventedChart.totalOfErasedDevices", {
                                        totalOfErasedDevices: displayedValuesAndUnits.total.toFixed(),
                                        unit: t(displayedValuesAndUnits.totalUnit),
                                    })}
                                </span>
                                <br />
                                {t("EsgDashboardView.referencedMonths", {
                                    firstMonth: commonFunctions.computeFirstMonth(this.props.widgetData),
                                    lastMonth: commonFunctions.computeLastMonth(this.props.widgetData),
                                })}
                                <br />
                                {t("EsgDashboardView.averagePerDay", {
                                    averagePerDay: displayedValuesAndUnits.average?.toFixed(2),
                                    unit: displayedValuesAndUnits.averageUnit
                                        ? t(displayedValuesAndUnits.averageUnit)
                                        : "",
                                    numberOfMonths: commonFunctions.fetchMonths(this.props.widgetData).length,
                                })}
                                <br />
                            </TextBlock>
                        </div>
                        <Bar
                            data={commonFunctions.getChartData(
                                t,
                                this.fetchLandfillWaste,
                                this.props.widgetData,
                                "bar"
                            )}
                            options={getChartOptions(
                                resolveColor("--app-chart-gridLineColor"),
                                resolveColor("--app-chart-textColor"),
                                this.props.convertToTonnes,
                                displayedValuesAndUnits.totalUnit,
                                t
                            )}
                        />
                    </div>
                )}
            </Translation>
        );
    }
}

function getChartOptions(
    gridLineColor: string,
    textColor: string,
    convert: boolean,
    unit: Units,
    t: TFunction
): ChartOptions<"bar"> {
    return {
        scales: {
            x: {
                type: "category",
                grid: {
                    display: false,
                },
                ticks: {
                    color: textColor,
                },
                stacked: true,
            },
            y: {
                type: "linear",
                display: true,
                grid: {
                    color: gridLineColor,
                },
                ticks: {
                    callback(n: number) {
                        return commonFunctions.toYAxisValueWithUnit(n, convert, unit, t(unit));
                    },
                    maxTicksLimit: 6,
                    color: textColor,
                },
                stacked: true,
            },
        },
        plugins: {
            tooltip: {
                enabled: true,
            },
            legend: {
                display: true,
                position: "bottom",
                labels: {
                    usePointStyle: true,
                    color: textColor,
                },
            },
            datalabels: {
                display: false,
            },
        },
        elements: {
            point: {
                radius: 0,
            },
        },
        responsive: true,
        maintainAspectRatio: false,
    };
}

export default LandfillWastePreventedChart;
